import type {
  UserGroupModulePermissionRequest,
  UserGroupModulePermissionResponse,
  UserGroupModulePermissionSchemaResponse,
  UserGroupObjectPermissionRequest,
  UserGroupObjectPermissionResponse,
  UserGroupObjectPermissionSchemaResponse,
  UserGroupPermissionSchemaModuleOperation,
  UserGroupPermissionSchemaObjectOperation,
  UserGroupRequest,
  UserGroupResponse,
  UserGroupSimpleResponse} from 'src/api/ProactApiClient';
import {
  ModuleEnum,
  PermissionOperationEnum
} from 'src/api/ProactApiClient';
import type {PillProps} from 'src/design/atoms/Pill';
import {IdpManagedPill} from 'src/shared/constants/PillConstants';
import type {ISelectOption} from 'src/types/Form';
import type {IFormDataV2, IFormFieldV2} from 'src/types/FormV2';

export const CreateDefaultUserGroup = (): IFormDataV2 => ({
  active: true,
  isNotificationGroup: true,
  isPermissionGroup: true,
  isDefaultEmployeeGroup: false,
  isDefaultContractorGroup: false,
  targetUsers: [],
  projectAccess: [],
});

export interface UserGroupFormMetaParams {
  personnel: ISelectOption[];
  projects: ISelectOption[];
}

export const UserGroupFormMeta = ({
  personnel,
  projects,
}: UserGroupFormMetaParams): IFormFieldV2[] => [
  {
    name: 'name',
    label: 'Name',
    type: 'text',
    required: true,
    validation: {
      minLength: 3,
      maxLength: 100,
    },
  },
  {
    name: 'active',
    label: 'Active',
    type: 'switch',
    required: true,
    switchOptions: {
      positiveText: 'Yes',
      negativeText: 'No',
    },
    description:
      'Enable or disable the group. If disabled, the group will be ignored when validating notifications or other functionality.',
  },
  {
    name: 'isNotificationGroup',
    label: 'Notification Group',
    type: 'switch',
    required: false,
    switchOptions: {
      positiveText: 'Yes',
      negativeText: 'No',
    },
    description:
      'If enabled, the group may be used as a target for notification rules.',
  },
  {
    name: 'isPermissionGroup',
    label: 'Permission Group',
    type: 'switch',
    required: false,
    switchOptions: {
      positiveText: 'Yes',
      negativeText: 'No',
    },
    description:
      'If enabled, the group may be used as a target for permissions.',
  },
  {
    name: 'isDefaultEmployeeGroup',
    label: 'Auto-Assign Group to Employees',
    type: 'switch',
    required: false,
    switchOptions: {
      positiveText: 'Yes',
      negativeText: 'No',
    },
    description:
      'If enabled, all employees are automatically assigned to this group. If later disabled, users are not removed from this group. Users will automatically be added to this group when they first sign up or if their employee type changes to "Employee".',
  },
  {
    name: 'isDefaultContractorGroup',
    label: 'Auto-Assign Group to Contractors',
    type: 'switch',
    required: false,
    switchOptions: {
      positiveText: 'Yes',
      negativeText: 'No',
    },
    description:
      'If enabled, all contractors are automatically assigned to this group. If later disabled, users are not removed from this group. Users will automatically be added to this group when they first sign up or if their employee type changes to "Contractor".',
  },
  {
    name: 'targetUsers',
    label: 'Group Members',
    type: 'select-multiple',
    required: false,
    options: personnel,
    description:
      'If your group members are managed through a Vendor auto-assignment group, then removing them here will not prevent them from being added again. Remove the user group from the Vendor first to ensure the users are not re-added to the group.',
  },
  {
    name: 'projectAccess',
    label: 'Project Access',
    type: 'select-multiple',
    required: false,
    options: projects,
    description:
      'Specify which projects this group has access to. Members of this group will be able to view data from these projects. Leave empty to allow access to all projects.',
  },
];

export const MatchModulePermissionSchemaToPermissions = (
  module: ModuleEnum,
  operation: UserGroupPermissionSchemaModuleOperation,
  permissions?: UserGroupModulePermissionResponse[]
) => {
  return permissions?.find(
    i =>
      i.module == module &&
      i.operation == operation.operation &&
      (i.operationName == operation.operationName ||
        (!i.operationName && !operation.operationName))
  );
};

export const UserGroupModulePermissionsFormMeta = (
  schema: UserGroupModulePermissionSchemaResponse
): IFormFieldV2[] => {
  const {modules} = schema;
  const fields: IFormFieldV2[] = [];

  modules.forEach(module => {
    fields.push({
      name: `${ModuleEnum[module.module]}-header`,
      label: module.name,
      type: 'heading',
    });

    module.operations.forEach(operation => {
      const name = GetModulePermissionFieldName(
        module.module,
        operation.operation,
        operation.operationName
      );

      fields.push({
        name: name,
        label: GetModulePermissionFieldLabel(operation),
        type: 'switch',
        switchOptions: {
          positiveText: 'Yes',
          negativeText: 'No',
        },
        description: operation.description,
      });
    });
  });

  return fields;
};

export const UserGroupObjectPermissionsFormMeta = (
  schema: UserGroupObjectPermissionSchemaResponse
): IFormFieldV2[] => {
  const {modules} = schema;
  const fields: IFormFieldV2[] = [];

  modules.forEach(module => {
    fields.push({
      name: `${ModuleEnum[module.module]}-header`,
      label: module.name,
      type: 'heading',
    });

    module.operations.forEach(operation => {
      const name = GetObjectPermissionFieldName(
        module.module,
        operation.objectId
      );

      fields.push({
        name: name,
        label: GetObjectPermissionFieldLabel(operation),
        type: 'switch',
        switchOptions: {
          positiveText: 'Yes',
          negativeText: 'No',
        },
      });
    });
  });

  return fields;
};

export const GetModulePermissionFieldLabel = (
  operation: UserGroupPermissionSchemaModuleOperation
) => {
  return operation.operationName
    ? operation.label ??
        `${PermissionOperationEnum[operation.operation]}-${
          operation.operationName
        }`
    : PermissionOperationEnum[operation.operation];
};

export const GetModulePermissionFieldName = (
  module: ModuleEnum,
  operation: PermissionOperationEnum,
  operationName?: string
) => {
  const name = !operationName ? 'std' : operationName;
  return `${module}-${operation}-${name}-scope`;
};

export const GetModulePermissionDetailsFromFieldName = (name: string) => {
  const strs = name.split('-');
  if (strs.length !== 4) {
    throw new Error(
      `Invalid module permission field name "${name}": expected format "{module}-{operation}-{operationName}-scope" with exactly 4 segments. Operation names must not contain hyphens — use underscores instead.`
    );
  }
  const module = strs[0];
  const operation = strs[1];
  const operationName = strs[2] === 'std' ? null : strs[2];

  return {module, operation, operationName};
};

export const GetObjectPermissionFieldLabel = (
  operation: UserGroupPermissionSchemaObjectOperation
) => {
  return operation.label ?? 'Unknown Object';
};

export const GetObjectPermissionFieldName = (
  module: ModuleEnum,
  id?: number
) => {
  return `${module}-${id ?? 0}-object-scope`;
};

export const GetObjectPermissionDetailsFromFieldName = (name: string) => {
  const strs = name.split('-');
  const module = strs[0];
  const objectId = strs[1];

  return {module, objectId};
};

export const MapUserGroupModulePermissionResponseToFormData = (
  data?: UserGroupModulePermissionResponse[]
): IFormDataV2 => {
  const obj: IFormDataV2 = {};

  if (!data) {
    return obj;
  }

  data.forEach(permission => {
    const name = GetModulePermissionFieldName(
      permission.module,
      permission.operation,
      permission.operationName
    );

    obj[name] = true;
  });

  return obj;
};

export const MapUserGroupObjectPermissionResponseToFormData = (
  data?: UserGroupObjectPermissionResponse[]
): IFormDataV2 => {
  const obj: IFormDataV2 = {};

  if (!data) {
    return obj;
  }

  data.forEach(permission => {
    const name = GetObjectPermissionFieldName(
      permission.module,
      permission.objectId
    );

    obj[name] = permission.hasAccess === true ? true : undefined;
  });

  return obj;
};

export const MapFormDataToUserGroupModulePermissions = (
  data?: IFormDataV2
): UserGroupModulePermissionRequest[] => {
  const objs: UserGroupModulePermissionRequest[] = [];

  if (!data) {
    return objs;
  }

  for (const [key, value] of Object.entries(data)) {
    const {module, operation, operationName} =
      GetModulePermissionDetailsFromFieldName(key);

    if (value) {
      const permission = {
        module: Number(module) as ModuleEnum,
        operation: Number(operation) as PermissionOperationEnum,
        operationName: operationName,
      } as UserGroupModulePermissionRequest;

      objs.push(permission);
    }
  }

  return objs;
};

export const MapFormDataToUserGroupObjectPermissions = (
  data?: IFormDataV2
): UserGroupObjectPermissionRequest[] => {
  const objs: UserGroupObjectPermissionRequest[] = [];

  if (!data) {
    return objs;
  }

  for (const [key, value] of Object.entries(data)) {
    const {module, objectId} = GetObjectPermissionDetailsFromFieldName(key);

    if (value) {
      const permission = {
        module: Number(module) as ModuleEnum,
        objectId: Number(objectId),
        hasAccess: value === true ? true : undefined,
      } as UserGroupObjectPermissionRequest;

      objs.push(permission);
    }
  }

  return objs;
};

export const MapUserGroupResponseToUserGroupRequest = (
  data: UserGroupResponse,
  targetUsers: number[]
): UserGroupRequest => {
  return {
    id: data.id,
    name: data.name,
    active: data.active,
    targetUsers: targetUsers,
    isNotificationGroup: data.isNotificationGroup,
    isPermissionGroup: data.isPermissionGroup,
  };
};

export const MatchObjectPermissionSchemaToPermissions = (
  module: ModuleEnum,
  objectId: number,
  permissions?: UserGroupObjectPermissionResponse[]
) => {
  return permissions?.find(i => i.module == module && i.objectId == objectId);
};

export const GetUserGroupPillConfig = (
  data?: UserGroupResponse | UserGroupSimpleResponse
): PillProps[] => {
  const pills: PillProps[] = [];
  if (data?.active === true) {
    pills.push({text: 'Active', color: 'green'});
  } else if (data?.active === false) {
    pills.push({text: 'Inactive', color: 'yellow'});
  }

  if (data?.isNotificationGroup === true) {
    pills.push({text: 'Notification Group', color: 'blue'});
  }

  if (data?.isPermissionGroup === true) {
    pills.push({text: 'Permissions Group', color: 'red'});
  }

  if (data?.isIdpManaged === true) {
    pills.push(IdpManagedPill);
  }

  return pills;
};

export const UserGroupDocumentTitle = 'Groups';
